import { useEffect, useMemo, useState } from 'react'
import { GoogleMap, Polygon } from '@react-google-maps/api'
import { adminApi, type ServiceableSectorPayload } from '../services/api'
import { useColors } from '../hooks/useColors'
import { useMapsReady } from '../hooks/useMapsReady'
import MapUnavailable from '../components/MapUnavailable'
import { GURUGRAM_CENTER, GURUGRAM_DEFAULT_ZOOM } from '../constants/geo'

type Zone = {
  id: string
  name: string
  base_delivery_fee: number
  surge_multiplier: number
  max_orders_per_day: number
  is_active: boolean
  polygon: Array<{ lat: number; lng: number }>
  color?: string
}

type Sector = {
  id: string
  name: string
  label?: string | null
  ring?: 'immediate' | 'mid' | 'outer' | null
  corridor?: string | null
  tier?: string | null
  sector_number?: number | null
  centroid_lat: number
  centroid_lng: number
  customer_selectable: boolean
  is_active: boolean
  display_order: number
}

const RING_LABELS: Record<string, string> = {
  immediate: 'Immediate (0–2 km)',
  mid: 'Nearby (2–4 km)',
  outer: 'Extended (4–6 km)',
}

const TIER_OPTIONS = ['PRIMARY', 'SECONDARY', 'ON_REQUEST']

const emptySectorForm = {
  name: '',
  centroid_lat: '',
  centroid_lng: '',
  corridor: '',
  tier: 'PRIMARY',
  customer_selectable: true,
}

/**
 * Defensively coerce a zone polygon into a valid Google Maps path. Tolerates a
 * double-encoded jsonb value (a JSON string instead of an array) and rejects
 * anything that isn't an array of ≥3 {lat,lng} points, so a single bad row can
 * never crash the page via <Polygon paths={...}/>.
 */
const toLatLngPath = (raw: unknown): Array<{ lat: number; lng: number }> | null => {
  let poly: unknown = raw
  if (typeof poly === 'string') {
    try {
      poly = JSON.parse(poly)
    } catch {
      return null
    }
  }
  if (!Array.isArray(poly)) return null
  const path = poly
    .map((p) => {
      const lat = Number((p as { lat?: unknown })?.lat)
      const lng = Number((p as { lng?: unknown })?.lng)
      return Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null
    })
    .filter((p): p is { lat: number; lng: number } => p !== null)
  return path.length >= 3 ? path : null
}

export default function DeliveryZonesPage() {
  const colors = useColors()
  const [rows, setRows] = useState<Zone[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all')
  const [sortBy, setSortBy] = useState<'name' | 'base_delivery_fee' | 'max_orders_per_day'>('name')
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc')
  const [page, setPage] = useState(1)
  const [points, setPoints] = useState<Array<{ lat: number; lng: number }>>([])
  const [zoneForm, setZoneForm] = useState({ name: '', base_delivery_fee: '0', surge_multiplier: '1', max_orders_per_day: '100', color: '#1f77b4' })
  const [saving, setSaving] = useState(false)
  const [overlapWarning, setOverlapWarning] = useState('')
  const [sectors, setSectors] = useState<Sector[]>([])
  const [sectorBusyId, setSectorBusyId] = useState<string | null>(null)
  const [sectorForm, setSectorForm] = useState({ ...emptySectorForm })
  const [sectorSaving, setSectorSaving] = useState(false)
  const [sectorError, setSectorError] = useState('')
  const [editingSectorId, setEditingSectorId] = useState<string | null>(null)
  const [editCentroid, setEditCentroid] = useState({ lat: '', lng: '' })
  const pageSize = 8

  const { keyMissing, isLoaded, loadError } = useMapsReady()

  const fetchRows = async () => {
    try {
      setLoading(true)
      setError('')
      const response = await adminApi.getDeliveryZones()
      const payload = response?.data ?? response
      setRows(Array.isArray(payload?.zones) ? payload.zones : [])
    } catch {
      setError('Unable to load delivery zones.')
    } finally {
      setLoading(false)
    }
  }

  const fetchSectors = async () => {
    try {
      const response = await adminApi.getServiceableSectors()
      const payload = response?.data ?? response
      setSectors(Array.isArray(payload?.sectors) ? payload.sectors : [])
    } catch {
      // Sectors are a secondary panel - don't block the page on failure.
      setSectors([])
    }
  }

  // Patch a sector field with optimistic update + revert on failure. Used by the
  // active / customer-selectable toggles.
  const patchSector = async (sector: Sector, patch: Partial<ServiceableSectorPayload>) => {
    setSectorBusyId(sector.id)
    setSectors((prev) => prev.map((s) => (s.id === sector.id ? { ...s, ...patch } : s)))
    try {
      await adminApi.updateServiceableSector(sector.id, patch)
    } catch {
      setSectors((prev) => prev.map((s) => (s.id === sector.id ? sector : s)))
    } finally {
      setSectorBusyId(null)
    }
  }

  const saveSectorCentroid = async (sector: Sector) => {
    const lat = Number(editCentroid.lat)
    const lng = Number(editCentroid.lng)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
      setSectorError('Centroid lat/lng must be valid numbers.')
      return
    }
    setSectorError('')
    await patchSector(sector, { centroid_lat: lat, centroid_lng: lng })
    setEditingSectorId(null)
  }

  const deleteSector = async (sector: Sector) => {
    if (!confirm(`Remove "${sector.name}" from serviceable areas? Customers there become non-serviceable.`)) return
    setSectorBusyId(sector.id)
    try {
      await adminApi.deleteServiceableSector(sector.id)
      setSectors((prev) => prev.filter((s) => s.id !== sector.id))
    } catch {
      setSectorError('Failed to delete sector.')
    } finally {
      setSectorBusyId(null)
    }
  }

  const createSector = async () => {
    const lat = Number(sectorForm.centroid_lat)
    const lng = Number(sectorForm.centroid_lng)
    if (!sectorForm.name.trim() || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      setSectorError('Name and valid centroid lat/lng are required.')
      return
    }
    setSectorSaving(true)
    setSectorError('')
    try {
      await adminApi.createServiceableSector({
        name: sectorForm.name.trim(),
        centroid_lat: lat,
        centroid_lng: lng,
        corridor: sectorForm.corridor.trim() || null,
        tier: sectorForm.tier || null,
        customer_selectable: sectorForm.customer_selectable,
        is_active: true,
      })
      setSectorForm({ ...emptySectorForm })
      await fetchSectors()
    } catch {
      setSectorError('Failed to create serviceable area.')
    } finally {
      setSectorSaving(false)
    }
  }

  const CORRIDOR_FALLBACK = 'Other'
  const sectorsByCorridor = useMemo(() => {
    const groups = new Map<string, Sector[]>()
    for (const s of sectors) {
      const key = s.corridor || (s.ring ? RING_LABELS[s.ring] || s.ring : CORRIDOR_FALLBACK)
      if (!groups.has(key)) groups.set(key, [])
      groups.get(key)!.push(s)
    }
    return Array.from(groups.entries()).map(([corridor, items]) => ({ corridor, items }))
  }, [sectors])

  useEffect(() => { void fetchRows(); void fetchSectors() }, [])

  const filtered = useMemo(() => {
    const base = rows
      .filter((r) => (statusFilter === 'all' ? true : statusFilter === 'active' ? r.is_active : !r.is_active))
      .filter((r) => r.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => {
        const A: any = a[sortBy]
        const B: any = b[sortBy]
        if (A < B) return sortOrder === 'asc' ? -1 : 1
        if (A > B) return sortOrder === 'asc' ? 1 : -1
        return 0
      })
    return base
  }, [rows, search, sortBy, sortOrder, statusFilter])

  const paged = filtered.slice((page - 1) * pageSize, page * pageSize)
  const pages = Math.max(1, Math.ceil(filtered.length / pageSize))

  const createZone = async () => {
    if (!zoneForm.name.trim() || points.length < 3) {
      setError('Zone name and at least 3 polygon points are required.')
      return
    }
    setSaving(true)
    try {
      const res = await adminApi.createDeliveryZone({
        name: zoneForm.name.trim(),
        base_delivery_fee: Number(zoneForm.base_delivery_fee || 0),
        surge_multiplier: Number(zoneForm.surge_multiplier || 1),
        max_orders_per_day: Number(zoneForm.max_orders_per_day || 100),
        polygon: points,
        color: zoneForm.color,
        is_active: true,
      })
      const payload = res?.data ?? res
      const overlaps = Array.isArray(payload?.overlaps) ? payload.overlaps : []
      setOverlapWarning(overlaps.length ? `Warning: overlaps ${overlaps.map((z: any) => z.name).join(', ')}` : '')
      setPoints([])
      setZoneForm({ name: '', base_delivery_fee: '0', surge_multiplier: '1', max_orders_per_day: '100', color: '#1f77b4' })
      await fetchRows()
    } catch {
      setError('Failed to create delivery zone.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <h2 className="font-display" style={{ margin: 0, color: colors.textPrimary }}>Delivery Zones &amp; Serviceable Areas</h2>
        <div style={{ marginTop: 4, color: colors.textMuted, fontSize: 13 }}>
          <strong>Serviceable Areas</strong> (bottom panel) decide where customers can order. Edits there take effect immediately across the app. The <strong>Delivery Zones</strong> polygons below control <strong>pricing, surge &amp; daily capacity only</strong> and do <em>not</em> gate serviceability.
        </div>
      </div>
      {overlapWarning ? <div style={{ color: '#b45309' }}>{overlapWarning}</div> : null}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 12 }}>
        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
          <h3 style={{ marginTop: 0 }}>Map-based Zone Editor</h3>
          <div style={{ marginBottom: 8, color: colors.textMuted }}>Click on map to add polygon points. Minimum 3 points.</div>
          {keyMissing ? (
            <MapUnavailable reason="keyMissing" height={360} />
          ) : loadError ? (
            <MapUnavailable reason="loadError" height={360} />
          ) : !isLoaded ? <div>Map loading...</div> : (
            <GoogleMap
              mapContainerStyle={{ width: '100%', height: '360px' }}
              center={GURUGRAM_CENTER}
              zoom={GURUGRAM_DEFAULT_ZOOM}
              onClick={(e) => {
                if (!e.latLng) return
                const point = { lat: e.latLng.lat(), lng: e.latLng.lng() }
                setPoints((prev) => [...prev, point])
              }}
            >
              {rows.map((z) => {
                const path = toLatLngPath(z.polygon)
                if (!path) return null
                return <Polygon key={z.id} paths={path} options={{ fillColor: z.color || '#2563eb', strokeColor: z.color || '#2563eb', fillOpacity: 0.2, strokeWeight: 2 }} />
              })}
              {points.length >= 2 ? <Polygon paths={points} options={{ fillColor: '#dc2626', strokeColor: '#dc2626', fillOpacity: 0.2, strokeWeight: 2 }} /> : null}
            </GoogleMap>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <button onClick={() => setPoints([])}>Clear points</button>
            <span style={{ color: colors.textMuted }}>{points.length} points</span>
          </div>
        </div>

        <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
          <h3 style={{ marginTop: 0 }}>Create Zone</h3>
          <input value={zoneForm.name} onChange={(e) => setZoneForm((f) => ({ ...f, name: e.target.value }))} placeholder="Name" style={{ width: '100%', marginBottom: 8 }} />
          <input value={zoneForm.base_delivery_fee} onChange={(e) => setZoneForm((f) => ({ ...f, base_delivery_fee: e.target.value }))} placeholder="Base Fee" style={{ width: '100%', marginBottom: 8 }} />
          <input value={zoneForm.surge_multiplier} onChange={(e) => setZoneForm((f) => ({ ...f, surge_multiplier: e.target.value }))} placeholder="Surge Multiplier" style={{ width: '100%', marginBottom: 8 }} />
          <input value={zoneForm.max_orders_per_day} onChange={(e) => setZoneForm((f) => ({ ...f, max_orders_per_day: e.target.value }))} placeholder="Max Orders/Day" style={{ width: '100%', marginBottom: 8 }} />
          <input type="color" value={zoneForm.color} onChange={(e) => setZoneForm((f) => ({ ...f, color: e.target.value }))} style={{ width: '100%', marginBottom: 8 }} />
          <button onClick={() => void createZone()} disabled={saving}>{saving ? 'Saving...' : 'Create Zone'}</button>
        </div>
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
          <input value={search} onChange={(e) => { setSearch(e.target.value); setPage(1) }} placeholder="Search zone" style={{ flex: 1 }} />
          <select value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value as any); setPage(1) }}><option value="all">All</option><option value="active">Active</option><option value="inactive">Inactive</option></select>
          <select value={sortBy} onChange={(e) => setSortBy(e.target.value as any)}><option value="name">Name</option><option value="base_delivery_fee">Base Fee</option><option value="max_orders_per_day">Max/Day</option></select>
          <select value={sortOrder} onChange={(e) => setSortOrder(e.target.value as any)}><option value="asc">Asc</option><option value="desc">Desc</option></select>
        </div>
        {loading ? <div style={{ padding: 20, color: colors.textMuted }}>Loading zones...</div> : error ? <div style={{ padding: 20, color: '#b91c1c' }}>{error}</div> : paged.length === 0 ? <div style={{ padding: 20, color: colors.textMuted }}>No zones found.</div> : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead><tr><th>Name</th><th>Base Fee</th><th>Surge</th><th>Max/Day</th><th>Status</th><th>Actions</th></tr></thead>
            <tbody>
              {paged.map((z) => (
                <tr key={z.id}>
                  <td>{z.name}</td><td>₹{Number(z.base_delivery_fee || 0).toFixed(2)}</td><td>{Number(z.surge_multiplier || 1).toFixed(2)}x</td><td>{z.max_orders_per_day}</td><td>{z.is_active ? 'Active' : 'Inactive'}</td>
                  <td style={{ display: 'flex', gap: 6 }}>
                    <button onClick={async () => { await adminApi.updateDeliveryZone(z.id, { is_active: !z.is_active }); await fetchRows() }}>{z.is_active ? 'Deactivate' : 'Activate'}</button>
                    <button onClick={async () => { if (confirm('Delete this zone?')) { await adminApi.deleteDeliveryZone(z.id); await fetchRows() } }}>Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        <div style={{ marginTop: 10, display: 'flex', justifyContent: 'space-between' }}>
          <button disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Prev</button>
          <span>Page {page} / {pages}</span>
          <button disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</button>
        </div>
      </div>

      <div style={{ background: colors.surface, border: `1px solid ${colors.border}`, borderRadius: 12, padding: 12 }}>
        <h3 style={{ marginTop: 0 }}>Serviceable Areas <span style={{ fontSize: 12, fontWeight: 400, color: colors.accent }}>· source of truth</span></h3>
        <div style={{ marginBottom: 12, color: colors.textMuted }}>
          These sectors are the single source of truth for serviceability. An active, customer-selectable sector appears in the customer “Areas we serve” list and makes any address within ~3.5&nbsp;km of its centroid serviceable (map pin, address save, and order placement all gate on this). Disable or remove one and customers there become non-serviceable immediately.
        </div>

        {sectorError ? <div style={{ color: '#b91c1c', marginBottom: 8 }}>{sectorError}</div> : null}

        {/* Add a serviceable area */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 8, alignItems: 'end', marginBottom: 14, padding: 10, border: `1px dashed ${colors.border}`, borderRadius: 8 }}>
          <label style={{ fontSize: 12, color: colors.textMuted }}>Name
            <input value={sectorForm.name} onChange={(e) => setSectorForm((f) => ({ ...f, name: e.target.value }))} placeholder="e.g. Sector 50" style={{ width: '100%' }} />
          </label>
          <label style={{ fontSize: 12, color: colors.textMuted }}>Centroid lat
            <input value={sectorForm.centroid_lat} onChange={(e) => setSectorForm((f) => ({ ...f, centroid_lat: e.target.value }))} placeholder="28.41" style={{ width: '100%' }} />
          </label>
          <label style={{ fontSize: 12, color: colors.textMuted }}>Centroid lng
            <input value={sectorForm.centroid_lng} onChange={(e) => setSectorForm((f) => ({ ...f, centroid_lng: e.target.value }))} placeholder="77.05" style={{ width: '100%' }} />
          </label>
          <label style={{ fontSize: 12, color: colors.textMuted }}>Corridor
            <input value={sectorForm.corridor} onChange={(e) => setSectorForm((f) => ({ ...f, corridor: e.target.value }))} placeholder="Sohna Road" style={{ width: '100%' }} />
          </label>
          <label style={{ fontSize: 12, color: colors.textMuted }}>Tier
            <select value={sectorForm.tier} onChange={(e) => setSectorForm((f) => ({ ...f, tier: e.target.value }))} style={{ width: '100%' }}>
              {TIER_OPTIONS.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <label style={{ fontSize: 12, color: colors.textMuted, display: 'flex', alignItems: 'center', gap: 6 }}>
            <input type="checkbox" checked={sectorForm.customer_selectable} onChange={(e) => setSectorForm((f) => ({ ...f, customer_selectable: e.target.checked }))} />
            Customer-selectable
          </label>
          <button onClick={() => void createSector()} disabled={sectorSaving}>{sectorSaving ? 'Adding…' : 'Add area'}</button>
        </div>

        {sectorsByCorridor.length === 0 ? (
          <div style={{ color: colors.textMuted }}>No serviceable areas yet. Add one above to start gating customers on it.</div>
        ) : sectorsByCorridor.map((group) => (
          <div key={group.corridor} style={{ marginBottom: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 6, color: colors.textPrimary }}>{group.corridor}</div>
            <div style={{ display: 'grid', gap: 6 }}>
              {group.items.map((s) => (
                <div key={s.id} style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8, padding: '6px 8px', border: `1px solid ${colors.border}`, borderRadius: 8, opacity: sectorBusyId === s.id ? 0.5 : 1 }}>
                  <span style={{ fontWeight: 600, color: colors.textPrimary, minWidth: 120 }}>{s.label || s.name}</span>
                  {editingSectorId === s.id ? (
                    <>
                      <input value={editCentroid.lat} onChange={(e) => setEditCentroid((c) => ({ ...c, lat: e.target.value }))} placeholder="lat" style={{ width: 90 }} />
                      <input value={editCentroid.lng} onChange={(e) => setEditCentroid((c) => ({ ...c, lng: e.target.value }))} placeholder="lng" style={{ width: 90 }} />
                      <button onClick={() => void saveSectorCentroid(s)} disabled={sectorBusyId === s.id}>Save</button>
                      <button onClick={() => setEditingSectorId(null)}>Cancel</button>
                    </>
                  ) : (
                    <span style={{ fontSize: 12, color: colors.textMuted }}>
                      {Number(s.centroid_lat).toFixed(4)}, {Number(s.centroid_lng).toFixed(4)}
                      <button
                        onClick={() => { setEditingSectorId(s.id); setEditCentroid({ lat: String(s.centroid_lat), lng: String(s.centroid_lng) }); setSectorError('') }}
                        style={{ marginLeft: 6, fontSize: 11 }}
                      >Edit centroid</button>
                    </span>
                  )}
                  <span style={{ flex: 1 }} />
                  <button
                    onClick={() => void patchSector(s, { is_active: !s.is_active })}
                    disabled={sectorBusyId === s.id}
                    style={{ background: s.is_active ? colors.accent : colors.bgSecondary, color: s.is_active ? colors.accentText : colors.textMuted, borderRadius: 999, border: `1px solid ${colors.border}`, padding: '3px 10px' }}
                    title={s.is_active ? 'Active. Click to disable' : 'Inactive. Click to enable'}
                  >{s.is_active ? 'Active' : 'Inactive'}</button>
                  <button
                    onClick={() => void patchSector(s, { customer_selectable: !s.customer_selectable })}
                    disabled={sectorBusyId === s.id}
                    style={{ borderRadius: 999, border: `1px solid ${colors.border}`, padding: '3px 10px', color: s.customer_selectable ? colors.textPrimary : colors.textMuted }}
                    title="Whether customers can pick this area from the list"
                  >{s.customer_selectable ? 'Selectable' : 'Hidden'}</button>
                  <button onClick={() => void deleteSector(s)} disabled={sectorBusyId === s.id} style={{ color: '#b91c1c' }}>Delete</button>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}
